import { computed } from "vue";
import { mapGetters, mapState } from "vuex";
import { canonical } from "@utils";
import { INTERLINK_SPECIAL_CASE, INTERLINK_TYPE, PAGES } from "@consts";
import { importSeoArticle } from "@/common/services/articleService";
import wordListMixin from "@/plugins/mixins/word-lists";

export default {
	name: "ResultTableWrapper",
	props: {
		isLoadingResults: {
			type: Boolean,
			default: false,
		},
	},
	mixins: [wordListMixin],
	inject: ["searchParams"],
	data() {
		return {
			isLoadingCopy: false,
			isError: false, // todo how do we handle errors visually?
		};
	},
	async fetch() {
		if (this.isLoadingResults) {
			return;
		}
		let response = {};
		const slug = this.getSlug();
		const title = this.cmsHeading;

		if (slug) {
			this.isLoadingCopy = true;
			response = await importSeoArticle(this.$axios, this.$config, slug);
		}

		if (!response?.sections?.length) {
			response.sections = this.getNonCMSCopySections();
		}

		this.$store.commit("query/saveSearch", {
			cmsHeading: title,
			cmsResponse: response,
		});

		this.isLoadingCopy = false;
	},
	head() {
		const metaTitle =
			this.response?.metaTitle ||
			this.getNonCMSWordListMetaInfo()?.metaTitle ||
			this.getNonCMSGameMetaInfo()?.metaTitle ||
			"";
		const metaDescription =
			this.response?.metaDescription ||
			this.getNonCMSWordListMetaInfo()?.metaDescription ||
			this.getNonCMSGameMetaInfo()?.metaDescription ||
			"";

		return {
			title: metaTitle,
			meta: [
				{
					name: "description",
					hid: "description",
					content: metaDescription,
				},
			],
			link: [canonical(this.$route)],
		};
	},
	mounted() {
		if (window.location.hash) {
			setTimeout(() => {
				this.$goToHash(window.location.hash);
			}, 300); // We have no choice but to delay a bit the execution to make sure we get the correct dimensions for the offsets
		}

		const cmsHeading = this.cmsHeading;

		if (cmsHeading !== this.aboveResultsTitle) {
			this.$store.commit("query/saveSearch", {
				cmsHeading,
			});

			this.$meta?.()?.refresh();
		}
	},
	computed: {
		...mapState("query", {
			response: (state) => state.cache.cmsResponse,
			aboveResultsTitle: (state) => state.cache.cmsHeading,
			interlinks: (state) => state.cache.interlinks,
			suggestedLinks: (state) => state.cache.suggestedLinks,
			totalResultsCount: (state) => state.cache.totalResultsCount,
			totalAlternativeResultsCount: (state) =>
				state.cache.totalAlternativeResultsCount,
		}),
		...mapGetters("navigation", [
			"isAnagramResultsPage",
			"isScrabbleDictionaryResultsPage",
			"isWordleResultsPage",
			"isUnscramblerResultsPage",
			"isQuordleResultsPage",
			"isAnagramResultsPage",
			"isNYTCrossplayDictionaryResultsPage",
			"isAnyWordListResultsPage",
		]),
		anagramWordsKey() {
			return Object.keys(this.anagramWords);
		},
		anagramWords() {
			return this.$store.state.query.cache.anagrams;
		},
		anagramsTotal() {
			return (
				this.anagramWords?.[this.anagramWordsKey]?._items?.length || 0
			);
		},
		hasAnagrams() {
			return this.anagramWords[this.anagramWordsKey]?._items;
		},
		hasOneAnagram() {
			return this.anagramsTotal === 1;
		},
		/**
		 * Lower case, per WFW-6748's acceptance criteria — the design mock
		 * renders it title case, but the ticket wording wins on copy.
		 *
		 * WFW-6700's QA caught a "1 words" heading on the word list pages, so
		 * the Wordle and Quordle headings guard the singular explicitly.
		 */
		resultsNoun() {
			return this.totalResultsCount === 1 ? "word" : "words";
		},
		cmsHeading() {
			if (this.isLoadingResults) {
				return "";
			}

			if (this.isWordleResultsPage) {
				return this.noResults
					? "Sorry there are no valid Wordle words using your search letters"
					: `${this.totalResultsCount} Wordle ${this.resultsNoun}`;
			}

			if (this.isQuordleResultsPage) {
				return this.noResults
					? "Sorry there are no valid Quordle words using your search letters"
					: `${this.totalResultsCount} Quordle ${this.resultsNoun}`;
			}

			if (this.isAnagramResultsPage) {
				const userInput = this.$route.params.tiles.toUpperCase();

				if (this.hasOneAnagram) {
					return `Full anagram result for <strong> ${userInput} </strong>`;
				}

				if (this.hasAnagrams) {
					return `${this.anagramsTotal} full anagram results for <strong> ${userInput} </strong>`;
				}

				if (this.noResults) {
					return "Sorry, there are no valid words for your search";
				}

				return `There are no full anagrams of <strong> ${userInput} </strong>`;
			}
			return this.isUnscramblerResultsPage
				? this.unscrambleResultsTitle
				: this.getH1Text();
		},
		isReady() {
			return !this.isLoadingCopy && !this.isLoadingResults;
		},
		hasSections() {
			return this.response?.sections?.length;
		},
		sections() {
			return this.hasSections ? this.response.sections : [];
		},
		unscrambleResultsTitle() {
			return this.noResults
				? "Sorry, there are no valid words for your search"
				: `Unscramble ${(this.searchParams?.tiles || "")
						.replace(/_/g, "?")
						.toUpperCase()}`;
		},
		aboveResultsCopy() {
			if (this.isLoadingCopy || this.isLoadingResults) {
				return "";
			}

			if (this.noResults) {
				return this.noResultsCopy;
			}

			// WFW-6756: intro copy below the H1 is removed on word-list pages
			// (words starting/ending/containing, by length, their combinations,
			// and the special curated variants). Other results pages
			// (unscramble, anagram, wordle, quordle, scrabble, NYT crossplay)
			// keep their intro copy.
			if (this.isAnyWordListResultsPage) {
				return null;
			}

			return this.hasSections ? this.sections[0] : null;
		},
		belowResultsCopy() {
			return this.hasSections ? this.sections[1] : null;
		},
		belowResultsAnchor() {
			return this.getBelowResultsAnchor();
		},
		shouldShowInterlinkDirector() {
			switch (this.$route.name) {
				case PAGES.WORDS_BY_LENGTH_RESULTS:
				case PAGES.WORDS_STARTING_RESULTS:
				case PAGES.WORDS_HAVING_RESULTS:
				case PAGES.WORDS_ENDING_RESULTS:
				case PAGES.WORDS_COMBINATION_LETTERS_RESULTS:
				case PAGES.WORDS_COMBINATION_LENGTH_AND_LETTERS_RESULTS:
					return true;
			}
			return false;
		},
		noResultsCopy() {
			/**
			 * @note to avoid the WFW-5565 PR from becoming waaaaay too big,
			 * I'm not refactoring this to use the new `getSearchParamsFromRoute`
			 * method just yet.
			 * Marking it as a @todo for now.
			 */
			const letterParam = this.$route.params.letter?.toUpperCase();

			switch (this.$route.name) {
				case PAGES.UNSCRAMBLER_RESULTS:
					if (this.totalAlternativeResultsCount > 0) {
						return "You're close! While there are no exact matches, adjusting the letters in your search can help you get more results. The following words add an extra letter that you might find on your game board.";
					}

					return "Try reducing or adjusting your advanced search options to yield more results.";
				case PAGES.ANAGRAM_RESULTS:
					return "You’re close! While there are no exact matches, modifying your search can help you get more results.";
				case PAGES.WORDLE_RESULTS:
				case PAGES.QUORDLE_RESULTS:
					return "You’re close! While there are no exact matches, adjusting the letters in your search can help you get more results.";
				case PAGES.WORDS_HAVING_RESULTS:
				case PAGES.WORDS_COMBINATION_LETTERS_RESULTS:
				case PAGES.WORDS_COMBINATION_LENGTH_AND_LETTERS_RESULTS: {
					const query =
						this.$route.name === PAGES.WORDS_HAVING_RESULTS
							? letterParam
							: this.aboveResultsTitle;
					return `<p>There are no <strong>${query}</strong>, you
					may want to try searching for a different combination of
					letters.</p><p>To browse word lists, please visit this <a
					href="/index/">index</a> page.</p>`;
				}

				case PAGES.WORDS_STARTING_RESULTS:
					return `<p>There are no words starting with
				"<strong>${letterParam}</strong>", you may want to try searching for
				a different combination of letters.</p><p>To browse word
				lists, please visit this <a href="/index/">index</a> page.</p>`;

				case PAGES.WORDS_ENDING_RESULTS:
					return `<p>There are no words ending with
				"<strong>${letterParam}</strong>", you may want to try searching for
				a different combination of letters.</p><p>To browse word
				lists, please visit this <a href="/index/">index</a> page.</p>`;

				case PAGES.WORDS_BY_LENGTH_RESULTS:
					return `<p>There are no
				<strong>${this.$route.params.length} Letter Words</strong>, you may want to try searching for
				a different combination of letters.</p><p>To browse word
				lists, please visit this <a href="/index/">index</a> page.</p>`;

				default:
					return "";
			}
		},
		interlinkDirectorValues() {
			const advancedSearchOptions = this.searchParams?.advanced || {};
			return {
				[INTERLINK_TYPE.STARTS]: advancedSearchOptions.starts,
				[INTERLINK_TYPE.ENDS]: advancedSearchOptions.ends,
				[INTERLINK_TYPE.WITH]: advancedSearchOptions.contains,
				[INTERLINK_TYPE.LENGTH]: advancedSearchOptions.length,
			};
		},
		interlinkSpecialCase() {
			return this.noResults ? INTERLINK_SPECIAL_CASE.FALLBACK : "";
		},
		noResults() {
			return this.totalResultsCount === 0;
		},
		hideCopy() {
			return (
				this.noResults &&
				this.isSmallDevice &&
				this.isUnscramblerResultsPage &&
				this.$store.state.navigation.firstTimeAccess
			);
		},
		mainContentStyles() {
			if (this.isSmallDevice) {
				return null;
			}

			const headerClearanceHeight =
				this.$store.getters["window/headerClearanceHeight"];

			return {
				top: `${headerClearanceHeight + 15}px`,
			};
		},
	},
	methods: {
		getNonCMSGameMetaInfo() {
			if (this.isUnscramblerResultsPage) {
				const tiles = this.$route.params.tiles?.toUpperCase() || "";

				const translationData = {
					UserQuery: tiles,
					numberQuery: this.totalResultsCount.toLocaleString(),
				};

				return {
					metaTitle: this.$t(
						"UnscrambleResultsMetaTitleText",
						translationData
					),
					metaDescription: this.$t(
						"UnscrambleResultsMetaDescriptionText",
						translationData
					),
				};
			} else if (this.isQuordleResultsPage || this.isWordleResultsPage) {
				return {
					metaTitle: this.$t(this.$route.meta.title),
					metaDescription: this.$t(this.$route.meta.description),
				};
			} else if (this.isScrabbleDictionaryResultsPage) {
				const tiles = this.$route.params.tiles || "";

				return {
					metaTitle: this.$t("ScrabbleDictionaryResultsMetaTitle", {
						Query: tiles.toUpperCase(),
					}),
					metaDescription: this.$t(
						"ScrabbleDictionaryResultsMetaDescription",
						{
							query: tiles,
						}
					),
				};
			} else if (this.isAnagramResultsPage) {
				return {
					metaTitle: this.$t("AnagramSolverResultsMetaTitle"),
					metaDescription: this.$t(
						"AnagramSolverResultsMetaDescription"
					),
				};
			} else if (this.isNYTCrossplayDictionaryResultsPage) {
				const userQuery = this.$route.params.tiles?.toUpperCase() || "";
				return {
					metaTitle: `Is ${userQuery} a Crossplay Word? | WordFinder® NYT Crossplay Dictionary`,
					metaDescription: this.$t(
						`Is ${userQuery} a valid word in NYT Crossplay? Find the Crossplay point value, definition, and see if it's accepted by the New York Times dictionary here.`
					),
				};
			}
		},
	},
	provide() {
		return {
			isCompactMode: computed(() => {
				const currentWidth = this.$store.state.window?.windowWidth || 0;
				return (
					currentWidth <= 345 ||
					(currentWidth >= 960 && currentWidth <= 1032)
				);
			}),
		};
	},
};
